import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import KeycloakService from '../../services/KeycloakService';
import {
  cancelAdditionalWfhQuotaRequest,
  createAdditionalWfhQuotaRequest,
  getAdditionalWfhQuotaRequests,
} from '../../services/AdditionalWfhQuotaService';
import { getEmployeeWfhQuota } from '../../admin/Services/EmployeePrivilegesService';
import {
  CANCEL_BUTTON_TEXT,
  ERROR_KEY,
  SHOW_NOTIFICATION,
  SUBMIT_BUTTON_TEXT,
  WFHAttribute,
} from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import GenericLoader from '../../components/Loader/GenericLoader';
import AdditionalWfhQuotaRequestCard from '../../components/AdditionalWfhQuota/AdditionalWfhQuotaRequestCard';

const QUARTER_OPTIONS = [
  { value: 0, label: 'Q1 (Jan–Mar)' },
  { value: 1, label: 'Q2 (Apr–Jun)' },
  { value: 2, label: 'Q3 (Jul–Sep)' },
  { value: 3, label: 'Q4 (Oct–Dec)' },
];

const currentQuarterIndex = () => Math.floor(new Date().getMonth() / 3);

/** Quarters available for the selected year (current + future only). */
const availableQuartersForYear = (year) => {
  const nowYear = new Date().getFullYear();
  const nowQ = currentQuarterIndex();
  return QUARTER_OPTIONS.filter((q) => {
    if (year > nowYear) return true;
    if (year < nowYear) return false;
    return q.value >= nowQ;
  });
};

const emptyForm = () => {
  const year = new Date().getFullYear();
  return {
    quotaYear: year,
    quarterIndex: currentQuarterIndex(),
    additionalDaysRequested: '',
    description: '',
  };
};

const AdditionalWfhQuotaComponent = () => {
  const currentUserId = KeycloakService.getUserDetail();
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [loader, setLoader] = useState(true);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [existingQuota, setExistingQuota] = useState(0);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const loadRequests = useCallback(async () => {
    setLoader(true);
    const response = await getAdditionalWfhQuotaRequests(
      currentUserId,
      {},
      pageNo,
      pageSize,
    );
    setLoader(false);
    if (!response.isError) {
      const pageData = response.data?.data || {};
      setRequests(pageData.content || []);
      setTotalPages(pageData.totalPages || 0);
    } else {
      notify(ERROR_KEY, response.message);
    }
  }, [currentUserId, pageNo, pageSize]);

  const loadExistingQuota = useCallback(async (year, quarterIndex) => {
    setQuotaLoading(true);
    const response = await getEmployeeWfhQuota(year, currentUserId);
    setQuotaLoading(false);
    if (!response.isError) {
      const days = response.data?.data?.quotaWFH?.thisYear?.[quarterIndex];
      setExistingQuota(typeof days === 'number' ? days : 0);
    } else {
      setExistingQuota(0);
    }
  }, [currentUserId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, refresh]);

  useEffect(() => {
    if (open) {
      loadExistingQuota(form.quotaYear, form.quarterIndex);
    }
  }, [open, form.quotaYear, form.quarterIndex, loadExistingQuota]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  };

  const additionalDays = Number(form.additionalDaysRequested) || 0;
  const resultingQuota = existingQuota + additionalDays;
  const yearOptions = [
    new Date().getFullYear(),
    new Date().getFullYear() + 1,
  ];
  const quarterOptions = availableQuartersForYear(form.quotaYear);

  const handleYearChange = (year) => {
    const options = availableQuartersForYear(year);
    const nextQuarter =
      options.find((q) => q.value === form.quarterIndex)?.value
      ?? options[0]?.value
      ?? currentQuarterIndex();
    setForm((prev) => ({
      ...prev,
      quotaYear: year,
      quarterIndex: nextQuarter,
    }));
  };

  const handleSubmit = async () => {
    if (!form.additionalDaysRequested || additionalDays <= 0) {
      notify(ERROR_KEY, 'Enter the number of additional WFH days you need for this quarter');
      return;
    }
    setLoading(true);
    const response = await createAdditionalWfhQuotaRequest({
      quotaYear: form.quotaYear,
      quarterIndex: form.quarterIndex,
      additionalDaysRequested: additionalDays,
      description: form.description || undefined,
    });
    setLoading(false);
    if (!response.isError) {
      notify('success', 'Additional WFH quota request submitted');
      setOpen(false);
      setForm(emptyForm());
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  const handleCancel = async (request) => {
    const response = await cancelAdditionalWfhQuotaRequest(
      request.id,
      currentUserId,
    );
    if (!response.isError) {
      notify('success', 'Request cancelled');
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: { xs: 0, sm: 1 } }}>
        Select a year and quarter and tell us how many
        <strong> additional WFH days</strong> you need for that quarter.
        Your Reporting Manager approves the additional days, which are then
        added to your existing quota for that quarter.
      </Typography>

      <Fab
        color="primary"
        size="medium"
        sx={{ right: 25, bottom: 80, position: 'fixed', zIndex: 1051 }}
        onClick={() => {
          setForm(emptyForm());
          setOpen(true);
        }}
      >
        <AddIcon />
      </Fab>

      {loader ? (
        <GenericLoader
          rectAttributes={WFHAttribute}
          width="100%"
          height={400}
        />
      ) : requests.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh',
          }}
        >
          <Typography color="text.secondary">
            No Additional WFH quota requests yet.
          </Typography>
        </Box>
      ) : (
        requests.map((req) => (
          <AdditionalWfhQuotaRequestCard
            key={req.id}
            request={req}
            variant="employee"
            onCancel={handleCancel}
          />
        ))
      )}

      {requests.length !== 0 && (
        <Box className="page-holder" sx={{ marginBottom: '10px' }}>
          <PaginationComponent
            count={totalPages}
            page={page}
            pageChange={pageChange}
            pageSize={pageSize}
            handlePageSizeChange={handlePageSizeChange}
          />
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Request Additional WFH Quota
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This increases your quarterly WFH entitlement. It is not a normal WFH
            date application.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="awfh-year-label">Year</InputLabel>
                <Select
                  labelId="awfh-year-label"
                  label="Year"
                  value={form.quotaYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="awfh-quarter-label">Quarter</InputLabel>
                <Select
                  labelId="awfh-quarter-label"
                  label="Quarter"
                  value={form.quarterIndex}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      quarterIndex: e.target.value,
                    }))
                  }
                >
                  {quarterOptions.map((q) => (
                    <MenuItem key={q.value} value={q.value}>
                      {q.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Additional WFH days for this quarter"
                inputProps={{ min: 1, step: 1 }}
                value={form.additionalDaysRequested}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    additionalDaysRequested: e.target.value,
                  }))
                }
                helperText={
                  quotaLoading
                    ? 'Loading current quota…'
                    : `Current quarterly quota: ${existingQuota} day(s). `
                      + (additionalDays > 0
                        ? `Requesting ${additionalDays} additional day(s) → new quota: ${resultingQuota} day(s).`
                        : 'Enter the number of additional days you need for this quarter.')
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Description (optional)"
                inputProps={{ maxLength: 1000 }}
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <LoadingButton onClick={() => setOpen(false)}>
              {CANCEL_BUTTON_TEXT}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={loading}
              onClick={handleSubmit}
            >
              {SUBMIT_BUTTON_TEXT}
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdditionalWfhQuotaComponent;
